import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  approveAbsence,
  downloadAbsenceAttachment,
  fetchAbsencePMApprovalQueue,
  fetchApprovalQueue,
  pmApproveAbsence,
  pmReturnAbsence,
  returnAbsence,
  type Absence,
} from "../api/client";
import { useBadges } from "../hooks/BadgesContext";
import { useQueueSort, type SortAccessors } from "../hooks/useQueueSort";
import { AbsenceRouteModal } from "./AbsenceRouteModal";
import { StatusBadge } from "./StatusBadge";
import { formatHours } from "../utils/format";

dayjs.locale("ru");

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD.MM.YYYY") : iso;
}

/** Дата подачи — с временем: заявки часто приходят пачкой в один день. */
function formatDateTime(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD.MM.YYYY HH:mm") : iso;
}

/** Порядок заявок в очереди (US-108).
 *
 * Здесь карточки, а не таблица, поэтому кликать не по чему — выбор вынесен
 * в отдельный список. Набор ключей тот же, что в очереди таймшитов: обе
 * очереди стоят на соседних вкладках, и разное поведение сбивало бы. */
type AbsenceSortKey = "employee" | "start" | "submitted";

const SORT_ACCESSORS: SortAccessors<Absence, AbsenceSortKey> = {
  employee: (a) => a.employee.full_name,
  start: (a) => a.start_date,
  submitted: (a) => a.submitted_at ?? null,
};

const SORT_OPTIONS: { value: AbsenceSortKey; label: string }[] = [
  { value: "submitted", label: "Сначала поданные раньше" },
  { value: "start", label: "По дате начала отсутствия" },
  { value: "employee", label: "По фамилии" },
];

interface ReturnModalProps {
  absence: Absence | null;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  busy: boolean;
}

function ReturnModal({ absence, onClose, onConfirm, busy }: ReturnModalProps) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [comment, setComment] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (absence) {
      setComment("");
      setTouched(false);
    }
  }, [absence]);

  const error = touched && comment.trim() === "";

  return (
    <Modal
      opened={!!absence}
      onClose={onClose}
      title="Вернуть заявку на доработку"
      centered={!isMobile}
      fullScreen={isMobile}
    >
      <Stack gap="sm">
        <Textarea
          label="Комментарий"
          placeholder="Опишите, что нужно исправить"
          autosize
          minRows={3}
          maxRows={8}
          value={comment}
          onChange={(e) => {
            setComment(e.currentTarget.value);
            setTouched(true);
          }}
          error={error ? "Комментарий обязателен" : undefined}
          required
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button
            color="red"
            onClick={() => {
              if (comment.trim() === "") {
                setTouched(true);
                return;
              }
              onConfirm(comment.trim());
            }}
            loading={busy}
            disabled={busy}
          >
            Вернуть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** US-107: та же карточка обслуживает две очереди. Этап РП отличается только
 *  источником данных и действиями — дублировать разметку незачем. */
export interface AbsenceApprovalQueueProps {
  /** "lr" — очередь линейного руководителя и HR (по умолчанию);
   *  "pm" — очередь руководителя проекта (ступень перед ЛР). */
  variant?: "lr" | "pm";
}

export function AbsenceApprovalQueue({ variant = "lr" }: AbsenceApprovalQueueProps = {}) {
  const isPm = variant === "pm";
  const [rawItems, setItems] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [returning, setReturning] = useState<Absence | null>(null);
  const [routeId, setRouteId] = useState<number | null>(null);

  // По умолчанию — кто подал раньше, тот выше: очередь разбирают сверху вниз.
  const {
    sorted: items,
    sort,
    setSort,
  } = useQueueSort<Absence, AbsenceSortKey>(rawItems, SORT_ACCESSORS, {
    storageKey: `absences.${variant}`,
    initial: { key: "submitted", direction: "asc" },
  });

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await (isPm ? fetchAbsencePMApprovalQueue() : fetchApprovalQueue());
      setItems(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [isPm]);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  // US-59 #8: refresh бейджей после approve/return — иначе очередь LR/HR
  // не сразу теряет цифру, цифра «своих действий» у автора тоже отстаёт.
  const { refresh: refreshBadges } = useBadges();

  // US-107: оговорка РП при согласовании — по одной на карточку.
  const [notes, setNotes] = useState<Record<number, string>>({});

  const handleApprove = useCallback(
    async (absence: Absence) => {
      setBusyId(absence.id);
      try {
        await (isPm
          ? pmApproveAbsence(absence.id, notes[absence.id]?.trim() || undefined)
          : approveAbsence(absence.id));
        void refreshBadges();
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [reload, refreshBadges, isPm, notes],
  );

  const handleReturn = useCallback(
    async (comment: string) => {
      if (!returning) return;
      const id = returning.id;
      setBusyId(id);
      try {
        await (isPm ? pmReturnAbsence(id, comment) : returnAbsence(id, comment));
        void refreshBadges();
        setReturning(null);
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [returning, reload, refreshBadges, isPm],
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fw={600}>
          {isPm ? "Отсутствия сотрудников моих проектов" : "Заявки на согласование"}
        </Text>
        {/* Прячем, когда сортировать нечего: на одной заявке выбор порядка —
            лишний элемент на экране. */}
        {rawItems.length > 1 && (
          <Group gap={6} wrap="nowrap">
            <Select
              size="xs"
              w={230}
              aria-label="Порядок заявок"
              data={SORT_OPTIONS}
              value={sort.key}
              allowDeselect={false}
              onChange={(value) =>
                value && setSort({ key: value as AbsenceSortKey, direction: sort.direction })
              }
            />
            <Button
              size="xs"
              variant="default"
              onClick={() =>
                setSort({ key: sort.key, direction: sort.direction === "asc" ? "desc" : "asc" })
              }
              aria-label={
                sort.direction === "asc"
                  ? "Порядок: по возрастанию, переключить на убывание"
                  : "Порядок: по убыванию, переключить на возрастание"
              }
            >
              {sort.direction === "asc" ? "▲" : "▼"}
            </Button>
          </Group>
        )}
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Stack align="center" py="xl">
          <Text c="dimmed">Нет заявок на согласование.</Text>
        </Stack>
      ) : (
        <Stack gap="sm">
          {items.map((a) => {
            const busy = busyId === a.id;
            return (
              <Card key={a.id} withBorder padding="sm" radius="sm">
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600}>{a.employee.full_name}</Text>
                      <Badge variant="light">{a.absence_type_display}</Badge>
                      <StatusBadge entity="absence" code={a.status} label={a.status_display} />
                      {a.is_delegated_for_me && (
                        <Badge
                          color="violet"
                          variant="light"
                          title="Эта заявка попала в Вашу очередь, поскольку Вы назначены делегатом."
                        >
                          Передано делегату
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {formatDate(a.start_date)} — {formatDate(a.end_date)}
                    </Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="sm">
                      Рабочих дней: <b>{a.working_days}</b>
                    </Text>
                    {a.calendar_days != null && (
                      <Text size="sm">
                        Календарных: <b>{a.calendar_days}</b>
                      </Text>
                    )}
                    <Text size="sm">
                      Часов: <b>{formatHours(a.hours)}</b>
                    </Text>
                    {/* US-108: очередь упорядочена по дате подачи — значит эту
                        дату надо и показать, иначе порядок нечем объяснить и
                        не с чем сверить. */}
                    {a.submitted_at && (
                      <Text size="sm" c="dimmed">
                        Подана: {formatDateTime(a.submitted_at)}
                      </Text>
                    )}
                  </Group>
                  {/* US-88: кто уже согласовал (ЛР / HR). В HR-очереди показывает
                      ЛР-согласанта; после HR-этапа (в истории) — обоих. */}
                  {(a.lr_approved_by_name || a.hr_approved_by_name) && (
                    <Text size="sm" c="dimmed">
                      Согласовал
                      {a.lr_approved_by_name ? ` ЛР: ${a.lr_approved_by_name}` : ""}
                      {a.lr_approved_by_name && a.hr_approved_by_name ? " · " : ""}
                      {a.hr_approved_by_name ? ` HR: ${a.hr_approved_by_name}` : ""}
                    </Text>
                  )}
                  {/* US-107: при нескольких РП заявка стоит, пока не подпишут
                      все — показываем, чьей подписи ещё ждём. */}
                  {isPm && (a.pm_pending_projects?.length ?? 0) > 1 && (
                    <Text size="sm" c="dimmed">
                      Ждёт согласования по проектам:{" "}
                      {a.pm_pending_projects?.map((p) => p.code).join(", ")}
                    </Text>
                  )}
                  {/* US-107: ЛР должен решать, зная позицию проекта, а не вслепую.
                      Оговорки приходят от подписей РП; при нескольких проектах
                      показываем, чья именно. */}
                  {!isPm &&
                    (a.pm_approvals ?? [])
                      .filter((p) => p.comment)
                      .map((p) => (
                        <Text size="sm" key={p.project_id}>
                          <Text span c="dimmed">
                            Комментарий РП{p.approved_by_name ? ` (${p.approved_by_name})` : ""}:{" "}
                          </Text>
                          {p.comment}
                        </Text>
                      ))}
                  {a.comment && (
                    <Text size="sm">
                      <Text span c="dimmed">
                        Комментарий заявителя:{" "}
                      </Text>
                      {a.comment}
                    </Text>
                  )}
                  {/* US-66: показываем кнопку «Скачать заявление» если файл
                      прикреплён. Permission на бэкенде — LR/HR/admin/owner. */}
                  {a.has_attachment && (
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm" c="dimmed">
                        📎 Файл-заявление:
                      </Text>
                      <Button
                        size="compact-xs"
                        variant="light"
                        onClick={() =>
                          downloadAbsenceAttachment(a.id, a.attachment_name ?? "absence.pdf").catch(
                            (err: unknown) =>
                              setError(
                                err instanceof Error ? err.message : "Не удалось скачать файл",
                              ),
                          )
                        }
                        data-testid={`approval-attach-${a.id}`}
                      >
                        Скачать{a.attachment_name ? ` (${a.attachment_name})` : ""}
                      </Button>
                    </Group>
                  )}
                  {/* US-107: оговорка не обязательна — согласование в один клик
                      остаётся основным сценарием, поле рядом на случай «согласую,
                      но период плотный». */}
                  {isPm && (
                    <Textarea
                      // Подсказки-примера нет намеренно (решение владелицы 17.08):
                      // пример в поле навязывает формулировку и повод, а руководитель
                      // сам знает, что написать. Подписи достаточно.
                      label="Комментарий для линейного руководителя (необязательно)"
                      autosize
                      minRows={1}
                      maxRows={4}
                      size="xs"
                      value={notes[a.id] ?? ""}
                      onChange={(e) => {
                        // Значение читаем ДО функционального обновления: к моменту
                        // выполнения апдейтера `currentTarget` уже null, и обращение
                        // к нему роняло всю очередь на первом же символе.
                        const value = e.currentTarget.value;
                        setNotes((prev) => ({ ...prev, [a.id]: value }));
                      }}
                      data-testid={`pm-approve-note-${a.id}`}
                    />
                  )}
                  <Group justify="flex-end" gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => setRouteId(a.id)}
                      data-testid={`approval-route-${a.id}`}
                    >
                      Маршрут
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => setReturning(a)}
                      disabled={busy}
                    >
                      Вернуть
                    </Button>
                    <Button
                      size="xs"
                      color="green"
                      onClick={() => handleApprove(a)}
                      loading={busy}
                      disabled={busy}
                    >
                      Согласовать
                    </Button>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      <ReturnModal
        absence={returning}
        busy={busyId === returning?.id}
        onClose={() => setReturning(null)}
        onConfirm={handleReturn}
      />
      <AbsenceRouteModal
        absenceId={routeId}
        opened={routeId != null}
        onClose={() => setRouteId(null)}
      />
    </Stack>
  );
}
